/**
 * SAMANVAI · Professor of Practice · Role-specific dashboard widgets
 * Each function returns the "actionable modules" column for a role.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Briefcase, ArrowRight, Users, Loader2, AlertTriangle, Star, LayoutDashboard,
  Plus, Calendar, TrendingUp, MessageCircleQuestion, ClipboardList, Compass,
  BookOpen, Sparkles, ShieldCheck, PlayCircle, GitBranch, BarChart3,
} from 'lucide-react';
import {
  popMyApplications, popMyInvitations, popReceivedApplications,
  popMyEnrollments, popMyBookings, popExpertImpact, popApplicationPipeline,
  popUniversityAnalytics, popListGaps, popListQuestions, popCreateSlot,
  popListSlots, popUpdateApplicationStatus, roleLabel,
} from '../../lib/popApi';

// ---------------------------------------------------------------------------
// Shared primitives
// ---------------------------------------------------------------------------
export const Card = ({ title, subtitle, icon: Icon, action, children, testId }) => (
  <div className="rounded-xl border border-white/10 bg-slate-900/40 p-5" data-testid={testId}>
    <div className="flex items-start justify-between mb-3">
      <div className="flex items-start gap-2">
        {Icon && <Icon className="w-4 h-4 text-amber-300 mt-0.5" />}
        <div>
          <div className="text-sm font-serif font-semibold text-white leading-tight">{title}</div>
          {subtitle && <div className="text-[10px] text-white/50 mt-0.5">{subtitle}</div>}
        </div>
      </div>
      {action}
    </div>
    {children}
  </div>
);

export const MicroStat = ({ Icon, label, value }) => (
  <div className="rounded-lg border border-white/10 bg-slate-900/40 p-3">
    <Icon className="w-3.5 h-3.5 text-amber-300 mb-1.5" />
    <div className="text-xl font-serif font-bold text-white tabular-nums leading-none">{value}</div>
    <div className="text-[9px] tracking-wider uppercase text-white/50 mt-1">{label}</div>
  </div>
);

const Empty = ({ children }) => <div className="text-xs text-white/50 italic">{children}</div>;

// ---------------------------------------------------------------------------
// INDUSTRY EXPERT DASHBOARD
// ---------------------------------------------------------------------------
export const ExpertDashboard = ({ user }) => {
  const [myApps, setMyApps] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [impact, setImpact] = useState(null);
  const [gaps, setGaps] = useState([]);
  const [slots, setSlots] = useState([]);
  const [slotBusy, setSlotBusy] = useState(false);
  const [slotForm, setSlotForm] = useState({ starts_at: '', duration_minutes: 30, topic: '' });
  const [slotOpen, setSlotOpen] = useState(false);

  const load = async () => {
    const [a, i, imp, g, s] = await Promise.all([
      popMyApplications().catch(() => ({ results: [] })),
      popMyInvitations().catch(() => ({ results: [] })),
      popExpertImpact().catch(() => null),
      popListGaps().catch(() => ({ results: [] })),
      popListSlots({ expert_user_id: user.user_id, upcoming_only: false }).catch(() => ({ results: [] })),
    ]);
    setMyApps(a.results || []); setInvitations(i.results || []); setImpact(imp);
    setGaps(g.results || []); setSlots(s.results || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const publishSlot = async () => {
    if (!slotForm.starts_at) return;
    setSlotBusy(true);
    try {
      await popCreateSlot({
        starts_at: new Date(slotForm.starts_at).toISOString(),
        duration_minutes: parseInt(slotForm.duration_minutes, 10) || 30,
        topic: slotForm.topic,
      });
      setSlotOpen(false);
      setSlotForm({ starts_at: '', duration_minutes: 30, topic: '' });
      load();
    } catch (_e) { /* noop */ }
    setSlotBusy(false);
  };

  return (
    <div className="space-y-5">
      {/* Impact ribbon */}
      {impact && (
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
          <MicroStat Icon={Calendar}      label="Slots"      value={impact.mentor_slots_published} />
          <MicroStat Icon={Users}         label="Bookings"   value={impact.bookings_total} />
          <MicroStat Icon={PlayCircle}    label="Sessions"   value={impact.sessions_completed} />
          <MicroStat Icon={BookOpen}      label="Content"    value={impact.content_published} />
          <MicroStat Icon={MessageCircleQuestion} label="Answers" value={impact.answers_given} />
          <MicroStat Icon={Star}          label="Rating"     value={impact.rating.average || '—'} />
        </div>
      )}

      {/* Mentorship slots */}
      <Card title="Mentorship slots" subtitle="Publish 1:1 windows students can book"
        icon={Calendar} testId="section-mentor-slots"
        action={<button onClick={() => setSlotOpen(!slotOpen)} data-testid="slot-open"
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-[10px] font-semibold bg-amber-300 hover:bg-amber-200 text-slate-900">
          <Plus className="w-3 h-3" /> Add slot
        </button>}>
        {slotOpen && (
          <div className="mb-3 rounded-lg border border-white/10 bg-slate-950 p-3 grid sm:grid-cols-3 gap-2">
            <input type="datetime-local" value={slotForm.starts_at} onChange={(e) => setSlotForm({ ...slotForm, starts_at: e.target.value })}
              data-testid="slot-form-when"
              className="px-2 py-1.5 rounded bg-white/5 border border-white/10 text-xs text-white" />
            <input type="number" value={slotForm.duration_minutes} onChange={(e) => setSlotForm({ ...slotForm, duration_minutes: e.target.value })}
              placeholder="Minutes"
              className="px-2 py-1.5 rounded bg-white/5 border border-white/10 text-xs text-white" />
            <input value={slotForm.topic} onChange={(e) => setSlotForm({ ...slotForm, topic: e.target.value })} placeholder="Topic (optional)"
              className="px-2 py-1.5 rounded bg-white/5 border border-white/10 text-xs text-white" />
            <button onClick={publishSlot} disabled={slotBusy || !slotForm.starts_at} data-testid="slot-submit"
              className="sm:col-span-3 inline-flex items-center justify-center gap-1 px-3 py-1.5 rounded bg-amber-300 hover:bg-amber-200 disabled:opacity-50 text-slate-900 text-xs font-semibold">
              {slotBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : null} Publish slot
            </button>
          </div>
        )}
        {slots.length === 0 ? <Empty>No slots published yet.</Empty> : (
          <ul className="space-y-1.5">
            {slots.slice(0, 4).map((s) => (
              <li key={s.id} className="flex items-center justify-between text-xs border-b border-white/5 pb-1.5">
                <div>
                  <div className="text-white/85">{new Date(s.starts_at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}</div>
                  <div className="text-[10px] text-white/50">{s.topic || 'General mentorship'} · {s.duration_minutes} min</div>
                </div>
                <span className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded ${s.is_booked ? 'bg-emerald-500/15 text-emerald-300' : 'bg-cyan-500/15 text-cyan-300'}`}>
                  {s.is_booked ? 'Booked' : 'Open'}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* Applications & invitations */}
      <Card title="Applications & invitations" icon={Briefcase} testId="section-my-apps"
        action={<Link to="/pop/opportunities" className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-300 hover:text-amber-200">
          Browse roles <ArrowRight className="w-3 h-3" />
        </Link>}>
        {myApps.length === 0 && invitations.length === 0 ? <Empty>None yet — apply or await an invitation.</Empty> : (
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-white/50 mb-1.5">Applications ({myApps.length})</div>
              {myApps.slice(0, 4).map((a) => (
                <div key={a.id} className="flex items-center justify-between text-xs border-b border-white/5 py-1.5">
                  <span className="text-white/85 truncate">Opp {a.opportunity_id.slice(0, 8)}…</span>
                  <span className="text-[9px] uppercase text-amber-300 tracking-wider">{a.status}</span>
                </div>
              ))}
              {myApps.length === 0 && <Empty>No applications yet.</Empty>}
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-white/50 mb-1.5">Invitations ({invitations.length})</div>
              {invitations.slice(0, 4).map((i) => (
                <div key={i.id} className="text-xs border-b border-white/5 py-1.5">
                  <div className="text-white/85 truncate">{i.message.slice(0, 50)}{i.message.length > 50 ? '…' : ''}</div>
                  <div className="text-[9px] uppercase text-amber-300 tracking-wider">{i.status}</div>
                </div>
              ))}
              {invitations.length === 0 && <Empty>No invitations yet.</Empty>}
            </div>
          </div>
        )}
      </Card>

      {/* Curriculum gaps you can close */}
      <Card title="Curriculum gaps you can close" icon={Compass} testId="section-expert-gaps"
        action={<Link to="/pop/gaps" className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-300 hover:text-amber-200">
          View all <ArrowRight className="w-3 h-3" />
        </Link>}>
        {gaps.length === 0 ? <Empty>No open gaps.</Empty> : gaps.slice(0, 3).map((g) => (
          <div key={g.id} className="text-xs border-b border-white/5 py-2">
            <div className="text-white/85 font-medium">{g.topic}</div>
            <div className="text-[10px] text-white/50">{g.course} · {g.flagged_by_institution || g.flagged_by_name}</div>
          </div>
        ))}
      </Card>
    </div>
  );
};

// ---------------------------------------------------------------------------
// UNIVERSITY / HEI DASHBOARD
// ---------------------------------------------------------------------------
export const UniversityDashboard = ({ user }) => {
  const [pipe, setPipe] = useState({ buckets: {} });
  const [analytics, setAnalytics] = useState(null);
  const [received, setReceived] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const load = async () => {
    const [p, a, r] = await Promise.all([
      popApplicationPipeline().catch(() => ({ buckets: {} })),
      popUniversityAnalytics().catch(() => null),
      popReceivedApplications().catch(() => ({ results: [] })),
    ]);
    setPipe(p); setAnalytics(a); setReceived(r.results || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const advance = async (appId, nextStatus) => {
    setBusy(true); setMsg('');
    try {
      await popUpdateApplicationStatus(appId, nextStatus, '');
      setMsg('Application moved to ' + nextStatus);
      load();
      setTimeout(() => setMsg(''), 2000);
    } catch (_e) { /* noop */ }
    setBusy(false);
  };

  const STAGES = [
    { id: 'submitted', label: 'Nomination',  next: 'screening' },
    { id: 'screening', label: 'Screening',   next: 'committee' },
    { id: 'committee', label: 'Committee',   next: 'approved' },
    { id: 'approved',  label: 'Council ✓',   next: null },
    { id: 'rejected',  label: 'Rejected',    next: null },
  ];

  return (
    <div className="space-y-5">
      {/* Analytics */}
      {analytics && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
          <MicroStat Icon={Briefcase}   label="Posted"      value={analytics.opportunities_posted} />
          <MicroStat Icon={TrendingUp}  label="Open"        value={analytics.opportunities_open} />
          <MicroStat Icon={ClipboardList} label="Applied"   value={analytics.applications_total} />
          <MicroStat Icon={GitBranch}   label="Committee"   value={analytics.applications_committee} />
          <MicroStat Icon={ShieldCheck} label="Approved"    value={analytics.applications_approved} />
        </div>
      )}

      {/* Funding route split */}
      {analytics && (
        <Card title="Engagement route split" icon={BarChart3} testId="section-route-split">
          <div className="grid grid-cols-3 gap-2">
            {Object.entries(analytics.route_split || {}).map(([k, v]) => (
              <div key={k} className="rounded-md border border-white/10 bg-slate-950 p-2.5">
                <div className="text-[9px] uppercase tracking-wider text-white/50">{k.replace('_', ' ')}</div>
                <div className="text-lg font-serif font-bold text-amber-300 tabular-nums">{v}</div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Pipeline */}
      <Card title="Committee pipeline" subtitle="Move applications from Nomination → Council approval"
        icon={GitBranch} testId="section-pipeline">
        <div className="overflow-x-auto -mx-2">
          <div className="min-w-[720px] grid grid-cols-5 gap-2 px-2">
            {STAGES.map((stg) => {
              const items = (pipe.buckets || {})[stg.id] || [];
              return (
                <div key={stg.id} className="rounded-lg border border-white/10 bg-slate-950 p-2" data-testid={`pipeline-col-${stg.id}`}>
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="text-[9px] uppercase tracking-wider font-bold text-amber-300">{stg.label}</div>
                    <span className="text-[10px] text-white/50 tabular-nums">{items.length}</span>
                  </div>
                  <div className="space-y-1">
                    {items.slice(0, 6).map((a) => (
                      <div key={a.id} className="rounded border border-white/5 bg-white/[0.02] p-1.5">
                        <div className="text-[11px] text-white/85 leading-tight truncate">{a.applicant?.name || 'Applicant'}</div>
                        <div className="text-[9px] text-white/50 truncate">{a.opportunity_title}</div>
                        {stg.next && (
                          <button onClick={() => advance(a.id, stg.next)} disabled={busy}
                            data-testid={`pipeline-advance-${a.id}`}
                            className="mt-1 inline-flex items-center gap-0.5 text-[9px] font-semibold text-amber-300 hover:text-amber-200">
                            → {stg.next}
                          </button>
                        )}
                      </div>
                    ))}
                    {items.length === 0 && <div className="text-[10px] text-white/40 italic px-1">Empty</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        {msg && <div className="text-[10px] text-emerald-300 mt-2">{msg}</div>}
      </Card>

      {/* Quick links */}
      <Card title="Actions" icon={LayoutDashboard} testId="section-uni-actions">
        <div className="grid sm:grid-cols-3 gap-2">
          <Link to="/pop/opportunities" className="rounded-lg border border-white/10 bg-slate-950 hover:bg-white/[0.03] p-3">
            <Briefcase className="w-4 h-4 text-amber-300 mb-1.5" />
            <div className="text-xs font-semibold text-white">Post an opportunity</div>
            <div className="text-[10px] text-white/50">Funded / Honorary / HEI</div>
          </Link>
          <Link to="/pop/directory?role=industry_expert&verified_only=true" className="rounded-lg border border-white/10 bg-slate-950 hover:bg-white/[0.03] p-3">
            <Users className="w-4 h-4 text-amber-300 mb-1.5" />
            <div className="text-xs font-semibold text-white">Discover experts</div>
            <div className="text-[10px] text-white/50">Nominate or invite directly</div>
          </Link>
          <Link to="/pop/gaps" className="rounded-lg border border-white/10 bg-slate-950 hover:bg-white/[0.03] p-3">
            <Compass className="w-4 h-4 text-amber-300 mb-1.5" />
            <div className="text-xs font-semibold text-white">Flag curriculum gaps</div>
            <div className="text-[10px] text-white/50">Course-level industry need</div>
          </Link>
        </div>
      </Card>
    </div>
  );
};

// ---------------------------------------------------------------------------
// FACULTY DASHBOARD
// ---------------------------------------------------------------------------
export const FacultyDashboard = ({ user }) => {
  const [gaps, setGaps] = useState([]);
  const [apps, setApps] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [slots, setSlots] = useState([]);

  useEffect(() => {
    (async () => {
      const [g, a, q, s] = await Promise.all([
        popListGaps().catch(() => ({ results: [] })),
        popMyApplications().catch(() => ({ results: [] })),
        popListQuestions({ limit: 6 }).catch(() => ({ results: [] })),
        popListSlots({ expert_user_id: user.user_id, upcoming_only: false }).catch(() => ({ results: [] })),
      ]);
      const mine = (g.results || []).filter((x) => x.flagged_by_user_id === user.user_id);
      setGaps(mine); setApps(a.results || []); setQuestions(q.results || []); setSlots(s.results || []);
    })();
    // eslint-disable-next-line
  }, []);

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <MicroStat Icon={Compass}     label="My Gaps"      value={gaps.length} />
        <MicroStat Icon={ClipboardList} label="My Applications" value={apps.length} />
        <MicroStat Icon={Calendar}    label="Slots Hosted" value={slots.length} />
        <MicroStat Icon={MessageCircleQuestion} label="Recent Q&A" value={questions.length} />
      </div>

      <Card title="My curriculum gaps" icon={Compass} testId="section-faculty-gaps"
        action={<Link to="/pop/gaps" className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-300 hover:text-amber-200">
          Flag gap <ArrowRight className="w-3 h-3" />
        </Link>}>
        {gaps.length === 0 ? <Empty>Flag your first curriculum gap and industry PoPs will step up.</Empty> :
          gaps.slice(0, 4).map((g) => (
            <div key={g.id} className="flex items-center justify-between text-xs border-b border-white/5 py-1.5">
              <div>
                <div className="text-white/85 font-medium">{g.topic}</div>
                <div className="text-[10px] text-white/50">{g.course}</div>
              </div>
              <span className="text-[10px] text-emerald-300 inline-flex items-center gap-1">
                <Users className="w-3 h-3" /> {g.interested_experts?.length || 0}
              </span>
            </div>
          ))
        }
      </Card>

      <Card title="Q&A activity" icon={MessageCircleQuestion} testId="section-faculty-qa"
        action={<Link to="/pop/questions" className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-300 hover:text-amber-200">
          Open board <ArrowRight className="w-3 h-3" />
        </Link>}>
        {questions.length === 0 ? <Empty>No community questions yet.</Empty> : questions.slice(0, 4).map((q) => (
          <div key={q.id} className="text-xs border-b border-white/5 py-1.5">
            <div className="text-white/85 truncate">{q.title}</div>
            <div className="text-[10px] text-white/50">{q.asker_name} · {q.answer_count} answers</div>
          </div>
        ))}
      </Card>

      <Card title="Quick actions" icon={LayoutDashboard} testId="section-faculty-actions">
        <div className="grid sm:grid-cols-3 gap-2">
          <Link to="/pop/library" className="rounded-lg border border-white/10 bg-slate-950 hover:bg-white/[0.03] p-3">
            <BookOpen className="w-4 h-4 text-amber-300 mb-1.5" />
            <div className="text-xs font-semibold text-white">Publish case study</div>
            <div className="text-[10px] text-white/50">Co-author with industry</div>
          </Link>
          <Link to="/pop/opportunities" className="rounded-lg border border-white/10 bg-slate-950 hover:bg-white/[0.03] p-3">
            <Briefcase className="w-4 h-4 text-amber-300 mb-1.5" />
            <div className="text-xs font-semibold text-white">Apply as PoP</div>
            <div className="text-[10px] text-white/50">HEI-funded routes</div>
          </Link>
          <Link to="/pop/directory?role=industry_expert" className="rounded-lg border border-white/10 bg-slate-950 hover:bg-white/[0.03] p-3">
            <Users className="w-4 h-4 text-amber-300 mb-1.5" />
            <div className="text-xs font-semibold text-white">Find co-designers</div>
            <div className="text-[10px] text-white/50">Peer review PoP work</div>
          </Link>
        </div>
      </Card>
    </div>
  );
};

// ---------------------------------------------------------------------------
// STUDENT DASHBOARD
// ---------------------------------------------------------------------------
export const StudentDashboard = ({ user }) => {
  const [enroll, setEnroll] = useState([]);
  const [book, setBook] = useState({ as_student: [] });
  const [questions, setQuestions] = useState([]);

  useEffect(() => {
    (async () => {
      const [e, b, q] = await Promise.all([
        popMyEnrollments().catch(() => ({ results: [] })),
        popMyBookings().catch(() => ({ as_student: [] })),
        popListQuestions({ limit: 6 }).catch(() => ({ results: [] })),
      ]);
      setEnroll(e.results || []); setBook(b); setQuestions(q.results || []);
    })();
  }, []);

  const upcoming = (book.as_student || []).filter((x) => ['requested', 'confirmed'].includes(x.status));
  const inProgress = enroll.filter((e) => e.status === 'in_progress');
  const completed = enroll.filter((e) => e.status === 'completed');

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <MicroStat Icon={BookOpen}    label="Enrolled"     value={enroll.length} />
        <MicroStat Icon={PlayCircle}  label="In Progress"  value={inProgress.length} />
        <MicroStat Icon={ShieldCheck} label="Completed"    value={completed.length} />
        <MicroStat Icon={Calendar}    label="Upcoming"     value={upcoming.length} />
      </div>

      <Card title="Continue learning" icon={PlayCircle} testId="section-student-learning"
        action={<Link to="/pop/learning" className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-300 hover:text-amber-200">
          My learning <ArrowRight className="w-3 h-3" />
        </Link>}>
        {inProgress.length === 0 && completed.length === 0 ? <Empty>No enrollments yet — head to the Library.</Empty> :
          [...inProgress, ...completed].slice(0, 4).map((e) => (
            <div key={e.id} className="text-xs border-b border-white/5 py-1.5">
              <div className="flex items-center justify-between">
                <div className="text-white/85 truncate">{e.subject_title}</div>
                <span className="text-[10px] text-amber-300 tabular-nums">{e.progress_pct}%</span>
              </div>
              <div className="h-1 rounded-full bg-white/10 mt-1 overflow-hidden">
                <div className="h-full bg-gradient-to-r from-amber-300 to-emerald-400" style={{ width: `${e.progress_pct}%` }} />
              </div>
            </div>
          ))
        }
      </Card>

      <Card title="Upcoming mentorship" icon={Calendar} testId="section-student-mentor"
        action={<Link to="/pop/directory?role=industry_expert&verified_only=true" className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-300 hover:text-amber-200">
          Find mentor <ArrowRight className="w-3 h-3" />
        </Link>}>
        {upcoming.length === 0 ? <Empty>No sessions booked yet.</Empty> :
          upcoming.slice(0, 3).map((b) => (
            <div key={b.id} className="text-xs border-b border-white/5 py-1.5">
              <div className="text-white/85">{b.expert_name}</div>
              <div className="text-[10px] text-white/50">{new Date(b.starts_at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}</div>
            </div>
          ))
        }
      </Card>

      <Card title="Ask a practitioner" icon={MessageCircleQuestion} testId="section-student-qa"
        action={<Link to="/pop/questions" className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-300 hover:text-amber-200">
          Q&amp;A board <ArrowRight className="w-3 h-3" />
        </Link>}>
        {questions.length === 0 ? <Empty>No community questions yet — be the first to ask.</Empty> :
          questions.slice(0, 4).map((q) => (
            <div key={q.id} className="text-xs border-b border-white/5 py-1.5">
              <div className="text-white/85 truncate">{q.title}</div>
              <div className="text-[10px] text-white/50">{q.answer_count} answers · {q.upvotes?.length || 0} upvotes</div>
            </div>
          ))
        }
      </Card>

      <Card title="Explore" icon={Sparkles} testId="section-student-explore">
        <div className="grid sm:grid-cols-3 gap-2">
          <Link to="/pop/library" className="rounded-lg border border-white/10 bg-slate-950 hover:bg-white/[0.03] p-3">
            <BookOpen className="w-4 h-4 text-amber-300 mb-1.5" />
            <div className="text-xs font-semibold text-white">Library</div>
            <div className="text-[10px] text-white/50">Courses · Research · Cases</div>
          </Link>
          <Link to="/pop/events" className="rounded-lg border border-white/10 bg-slate-950 hover:bg-white/[0.03] p-3">
            <Calendar className="w-4 h-4 text-amber-300 mb-1.5" />
            <div className="text-xs font-semibold text-white">Events</div>
            <div className="text-[10px] text-white/50">Webinars · Workshops · Office hours</div>
          </Link>
          <Link to="/pop/directory" className="rounded-lg border border-white/10 bg-slate-950 hover:bg-white/[0.03] p-3">
            <Users className="w-4 h-4 text-amber-300 mb-1.5" />
            <div className="text-xs font-semibold text-white">Directory</div>
            <div className="text-[10px] text-white/50">Verified practitioners</div>
          </Link>
        </div>
      </Card>
    </div>
  );
};

// ---------------------------------------------------------------------------
// INDUSTRY ORG DASHBOARD
// ---------------------------------------------------------------------------
export const OrgDashboard = ({ user }) => (
  <div className="space-y-5">
    <Card title="Sponsor & nominate" icon={Sparkles} testId="section-org-actions">
      <div className="grid sm:grid-cols-3 gap-2">
        <Link to="/pop/directory?role=industry_expert" className="rounded-lg border border-white/10 bg-slate-950 hover:bg-white/[0.03] p-3">
          <Users className="w-4 h-4 text-amber-300 mb-1.5" />
          <div className="text-xs font-semibold text-white">Nominate experts</div>
          <div className="text-[10px] text-white/50">From your organisation</div>
        </Link>
        <Link to="/pop/opportunities" className="rounded-lg border border-white/10 bg-slate-950 hover:bg-white/[0.03] p-3">
          <Briefcase className="w-4 h-4 text-amber-300 mb-1.5" />
          <div className="text-xs font-semibold text-white">Sponsor a role</div>
          <div className="text-[10px] text-white/50">Industry-funded route</div>
        </Link>
        <Link to="/pop/library" className="rounded-lg border border-white/10 bg-slate-950 hover:bg-white/[0.03] p-3">
          <BookOpen className="w-4 h-4 text-amber-300 mb-1.5" />
          <div className="text-xs font-semibold text-white">Publish content</div>
          <div className="text-[10px] text-white/50">Case studies · research</div>
        </Link>
      </div>
    </Card>
    <Card title="Ecosystem trends" icon={TrendingUp} testId="section-org-trends">
      <div className="text-xs text-white/60">
        Discover which sectors have the highest curriculum-gap flagging, and where your experts can create outsized impact.
      </div>
      <Link to="/pop/gaps" className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-300 hover:text-amber-200 mt-2">
        View curriculum gap board <ArrowRight className="w-3 h-3" />
      </Link>
    </Card>
  </div>
);
